// What the maintenance worklist shows, and — when a row is missing from it —
// which control is responsible.
//
// Lifted out of `MyListTab.tsx` so it can be tested: `vitest.config.ts` runs
// `src/**/*.test.ts` in a node environment with no DOM, so a predicate living
// inside a .tsx is unreachable by any test. These are the same rules the tab
// applied before, moved verbatim.

import { eventMeta, UNASSIGNED_LABEL, type EventTypeCatalog } from '@/types/maintenanceTask'
import type { MaintenanceTask, TaskFilter } from '@/types/maintenanceTask'
import type { ListEventsQuery } from '@/api/equipmentApi'
import type { EquipmentPurpose } from '@/types/equipment'
import { isWithinDisplayRange, toQueryRange, type DisplayRange } from './taskDateRange'

/**
 * Inventory's Tasks lists storage units', materials' and purchase orders' tasks
 * (z8nrz7fjzc): 'all' of them, the 'storage' units' only, the 'orders' only,
 * or one material type's.
 */
export type TaskSubject = 'all' | 'storage' | 'orders' | (string & {})

/** What the feeds ask for under a subject: units of the page's purposes, materials of its types, orders, or one of them. */
export function subjectQuery(
  subject: TaskSubject,
  purposes: readonly EquipmentPurpose[],
  materialTypes?: readonly string[],
  orders?: boolean,
): { purposes?: readonly EquipmentPurpose[]; materialTypes?: readonly string[]; orders?: boolean } {
  if (!materialTypes?.length || subject === 'all') return { purposes, materialTypes, ...(orders ? { orders } : {}) }
  if (subject === 'storage') return { purposes }
  if (subject === 'orders') return { orders: true }
  return { materialTypes: [subject] }
}

/** Does the subject filter let this task through? A material's type matches without regard to case. */
export function matchesSubject(task: MaintenanceTask, subject: TaskSubject): boolean {
  if (subject === 'all') return true
  if (subject === 'storage') return !task.material && !task.purchaseOrder
  if (subject === 'orders') return !!task.purchaseOrder
  return task.material?.type.toLowerCase() === subject.toLowerCase()
}

/** Does the chip row let this task through? */
export function matchesFilter(task: MaintenanceTask, filter: TaskFilter): boolean {
  switch (filter) {
    case 'due':
      return task.status === 'due'
    case 'started':
      return task.status === 'started'
    case 'overdue':
      // The flag, not the status: a started task can be overdue (z8nrz7dyc7).
      return task.overdue === true
    case 'pm':
      return task.taskClass === 'preventive'
    case 'cm':
      return task.taskClass === 'corrective'
    case 'process':
      return task.taskClass === 'process'
    case 'assigned':
      return task.assigned !== UNASSIGNED_LABEL
    case 'unassigned':
      return task.assigned === UNASSIGNED_LABEL
    default:
      return true
  }
}

/**
 * Does the search box let this task through?
 *
 * Searches EVERY COLUMN THE TABLE SHOWS, not a subset of them. Assigned and SOP
 * were both missing once, so searching a technician's name returned nothing on
 * a worklist that was displaying it in a column — and the placeholder had been
 * promising SOP the whole time. A search that silently ignores a visible column
 * reads as broken data rather than a narrow search.
 */
export function matchesSearch(task: MaintenanceTask, search: string, catalog?: EventTypeCatalog): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true
  return [
    task.equipmentId,
    task.usedBy,
    task.assigned,
    task.sopRef,
    // What the row shows: the tenant's name for the type (z8nrz7fb9k).
    eventMeta(task.eventType, catalog).label,
  ].some((field) => !!field && field.toLowerCase().includes(q))
}

/** Which of the controls is keeping a task off screen. */
export type HiddenReason = 'range' | 'filter' | 'search' | 'resource' | 'subject'

/**
 * Every reason this task is absent from the worklist right now — empty when it
 * is on screen.
 *
 * All three can hide the same row at once, and clearing only one of them leaves
 * it just as invisible, so the caller is told about each rather than the first.
 * This exists so creating a task can never look like nothing happened: a task
 * saved outside the dates on screen, or excluded by the active chip, is real
 * but unseen, and silence there reads as a failed save.
 */
export function hiddenReasons(
  task: MaintenanceTask,
  range: DisplayRange,
  filter: TaskFilter,
  search: string,
  catalog?: EventTypeCatalog,
  /** The Resource filter (z8nrz7fgzw): one person's tasks, or null for everyone. */
  resourceId: string | null = null,
  /** Inventory's item filter (z8nrz7fjzc). */
  subject: TaskSubject = 'all',
): HiddenReason[] {
  const reasons: HiddenReason[] = []
  // The Overdue chip lists every overdue task whatever the dates on screen, so
  // the range cannot hide a row the chip lets through (see `worklistQuery`).
  // It still counts for a row the chip excludes: clearing the chip hands that
  // row back to the range, and "Show it" must clear both in one go.
  const listedByOverdueChip = filter === 'overdue' && matchesFilter(task, filter)
  if (!listedByOverdueChip && !isWithinDisplayRange(task.dueDate, range)) reasons.push('range')
  if (!matchesFilter(task, filter)) reasons.push('filter')
  if (!matchesSearch(task, search, catalog)) reasons.push('search')
  if (resourceId && task.assigneeId !== resourceId) reasons.push('resource')
  if (!matchesSubject(task, subject)) reasons.push('subject')
  return reasons
}

/**
 * What the worklist asks the server for.
 *
 * The Overdue chip asks for the overdue set itself, with no date range: overdue
 * work is in the past, so filtering the current month's rows would hide a task
 * that went overdue last month (z8nrz7dyc7 AC5). Every other chip filters the
 * rows of the chosen period.
 */
export const OVERDUE_QUERY: ListEventsQuery = { overdue: true }

/**
 * How many tasks the All view shows: the latest by due date. Every routine
 * task is created ahead of time now (z8nrz7f9u6), so a tenant's history grows
 * every day and "everything" would be downloaded in full; past this the page
 * says so and points at the date range.
 */
export const ALL_TASKS_LIMIT = 1000

export function worklistQuery(range: DisplayRange, filter: TaskFilter): ListEventsQuery {
  if (filter === 'overdue') return OVERDUE_QUERY
  // One over the limit, to know whether there is more than the page shows.
  return range ? toQueryRange(range) : { limit: ALL_TASKS_LIMIT + 1 }
}
